import { useCallback, useState, useEffect, useRef } from "react";
import AwsS3 from "@uppy/aws-s3";
import Uppy from "@uppy/core";
import { DragDrop, useUppy } from "@uppy/react";
import useGenerateUploadCustomEventFileUrl from "hooks/customEvent/useGenerateUploadCustomEventFileUrl";
import useModalState from "hooks/useModalState";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import { Grid, Modal, IconButton } from "@mui/material";
import {
  Close as CloseIcon,
  Visibility as VisibilityIcon,
  Delete as DeleteIcon,
} from "@mui/icons-material";
import isImage from "utils/images/isImage";
import { CustomEventFile, CustomEventFileInput } from "generated/graphql";

import "@uppy/core/dist/style.css";
import "@uppy/drag-drop/dist/style.css";

interface FileChangeData {
  newFiles: CustomEventFileInput[];
  removedFileIds: string[];
}

interface AddFilesToCustomEventProps {
  onFilesChange: (data: FileChangeData) => void;
  existingFiles?: CustomEventFile[];
}

export default function AddFilesToCustomEvent({
  onFilesChange,
  existingFiles = [],
}: AddFilesToCustomEventProps) {
  const [generateUploadCustomEventFileUrl, { getData, isSuccess }] =
    useGenerateUploadCustomEventFileUrl();

  const [newFiles, setNewFiles] = useState<CustomEventFileInput[]>([]);
  const [removedFileIds, setRemovedFileIds] = useState<string[]>([]);
  const [previews, setPreviews] = useState<{ [key: string]: string }>({});
  const previewsRef = useRef<{ [key: string]: string }>({});
  const [uploadProgress, setUploadProgress] = useState<{ completed: number; total: number }>({
    completed: 0,
    total: 0,
  });
  const [errorMessage, setErrorMessage] = useState("");
  const { open: modalOpen, onOpen: openModal, onClose: closeModal } = useModalState();
  const [selectedImage, setSelectedImage] = useState<{ src: string; name: string } | null>(null);

  useEffect(() => {
    const existingPreviews: { [key: string]: string } = {};
    existingFiles.forEach((file) => {
      if (isImage(file.name)) {
        existingPreviews[file.name] = `${import.meta.env.VITE_CDN_BASE_URL}/${file.path}`;
      }
    });
    previewsRef.current = { ...previewsRef.current, ...existingPreviews };
    setPreviews(existingPreviews);
  }, [existingFiles]);

  useEffect(() => {
    onFilesChange({
      newFiles,
      removedFileIds,
    });
  }, [newFiles, removedFileIds, onFilesChange]);

  const onSuccess = useCallback((file: { name: string; path: string }) => {
    setNewFiles((prevFiles) => [...prevFiles, file]);
  }, []);

  const readImage = useCallback((file: any, fileName: string) => {
    const blob = new Blob([file.data], { type: file.type });
    const reader = new FileReader();
    reader.onload = () => {
      if (reader.result && typeof reader.result === "string") {
        previewsRef.current = { ...previewsRef.current, [fileName]: reader.result };
        setPreviews({ ...previewsRef.current });
      }
    };
    reader.readAsDataURL(blob);
  }, []);

  const handleViewImage = useCallback(
    (imageSrc: string, fileName: string) => {
      setSelectedImage({ src: imageSrc, name: fileName });
      openModal();
    },
    [openModal]
  );

  const handleCloseModal = useCallback(() => {
    closeModal();
    setSelectedImage(null);
  }, [closeModal]);

  const uppy = useUppy(() => {
    const _uppy = new Uppy({
      autoProceed: true,
      restrictions: {
        allowedFileTypes: ["image/*", ".jpg", ".jpeg", ".png", ".pdf"],
      },
    }).use(AwsS3, {
      async getUploadParameters(file) {
        const result = await generateUploadCustomEventFileUrl({
          variables: {
            contentType: file.type,
            filename: file.name,
          },
        });
        const success = isSuccess(result.data);
        if (success) {
          const data = getData(result);
          return {
            method: data.method as "PUT",
            url: data.url,
            fields: {},
            headers: {
              "Content-Type": data.contentType,
              ...(data.contentDisposition && { "Content-Disposition": data.contentDisposition }),
            },
          };
        } else {
          throw new Error("Could not upload the file");
        }
      },
    });

    _uppy.on("upload", (data) => {
      const fileCount = data?.fileIDs?.length || 0;
      setUploadProgress({ completed: 0, total: fileCount });
      setErrorMessage("");
    });

    _uppy.on("upload-success", (file, response) => {
      if (response.status === 200) {
        const path = "tmp/" + response.uploadURL.split("/tmp/")[1];
        onSuccess({ name: file.name, path });
        setUploadProgress((prev) => {
          const newCompleted = prev.completed + 1;
          if (newCompleted >= prev.total) {
            return { completed: 0, total: 0 };
          }
          return { ...prev, completed: newCompleted };
        });
        if (file.type.startsWith("image/")) {
          readImage(file, file.name);
        }
        _uppy.removeFile(file.id);
      }
    });

    _uppy.on("complete", () => {
      setUploadProgress({ completed: 0, total: 0 });
    });

    _uppy.on("restriction-failed", (file, error) => {
      if (error) {
        setErrorMessage(error.message);
        setUploadProgress({ completed: 0, total: 0 });
      }
    });

    _uppy.on("upload-error", (file) => {
      if (file) {
        setErrorMessage("An error has occurred while uploading this file");
        setUploadProgress((prev) => ({
          completed: prev.completed + 1,
          total: prev.total,
        }));
        _uppy.removeFile(file.id);
      }
    });

    return _uppy;
  });

  const removeFile = useCallback(
    (index: number, isExisting: boolean = false) => {
      if (isExisting) {
        const fileToRemove = existingFiles[index];
        if (fileToRemove) {
          setRemovedFileIds((prev) => [...prev, fileToRemove.id]);
          delete previewsRef.current[fileToRemove.name];
          setPreviews((prev) => {
            const newPreviews = { ...prev };
            delete newPreviews[fileToRemove.name];
            return newPreviews;
          });
        }
      } else {
        const fileToRemove = newFiles[index];
        const updatedNewFiles = newFiles.filter((_, i) => i !== index);
        setNewFiles(updatedNewFiles);

        if (fileToRemove) {
          delete previewsRef.current[fileToRemove.name];
          setPreviews((prev) => {
            const newPreviews = { ...prev };
            delete newPreviews[fileToRemove.name];
            return newPreviews;
          });
        }
      }
    },
    [newFiles, existingFiles]
  );

  const visibleExistingFiles = existingFiles.filter((file) => !removedFileIds.includes(file.id));
  const totalFiles = visibleExistingFiles.length + newFiles.length;

  return (
    <MDBox>
      <MDTypography variant="h6" mb={2}>
        Attachments
      </MDTypography>

      <MDBox mb={2}>
        <DragDrop
          width="100%"
          height="150px"
          uppy={uppy}
          locale={{
            strings: {
              dropHereOr: "Drop files here or %{browse}",
              browse: "browse",
            },
          }}
        />
      </MDBox>

      {uploadProgress.total > 0 && (
        <MDTypography variant="body2" color="info" mb={1}>
          Uploading {uploadProgress.completed}/{uploadProgress.total}...
        </MDTypography>
      )}

      {errorMessage && (
        <MDTypography variant="body2" color="error" mb={1}>
          {errorMessage}
        </MDTypography>
      )}

      {totalFiles > 0 && (
        <MDBox>
          <MDTypography variant="body2" mb={1}>
            Files:
          </MDTypography>
          <Grid container spacing={1}>
            {visibleExistingFiles.map((file, index) => (
              <Grid item xs={12} sm={6} md={4} key={`existing-${file.id}`}>
                <MDBox
                  sx={{
                    pt: 2,
                    pb: 2,
                    pl: 2,
                    pr: 1,
                    border: "1px solid #e0e0e0",
                    borderRadius: "4px",
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    width: "100%",
                    minHeight: "64px",
                  }}
                >
                  {previews[file.name] && (
                    <MDBox
                      component="img"
                      src={previews[file.name]}
                      alt={file.name}
                      width="40px"
                      height="40px"
                      borderRadius="4px"
                      sx={{ objectFit: "cover", flexShrink: 0 }}
                    />
                  )}
                  <MDBox flexGrow={1} sx={{ minWidth: 0 }}>
                    <MDTypography
                      variant="caption"
                      fontWeight="medium"
                      sx={{
                        lineHeight: 1.2,
                        wordBreak: "break-word",
                        display: "-webkit-box",
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: "vertical",
                        overflow: "hidden",
                      }}
                    >
                      {file.name}
                    </MDTypography>
                  </MDBox>
                  <MDBox display="flex" gap="4px">
                    {previews[file.name] && (
                      <MDButton
                        variant="text"
                        color="info"
                        size="small"
                        onClick={() => handleViewImage(previews[file.name], file.name)}
                        startIcon={<VisibilityIcon />}
                        sx={{ minWidth: "auto", p: 0.5 }}
                      ></MDButton>
                    )}
                    <MDButton
                      variant="text"
                      color="error"
                      size="small"
                      onClick={() => removeFile(index, true)}
                      startIcon={<DeleteIcon />}
                      sx={{ minWidth: "auto", p: 0.5 }}
                    ></MDButton>
                  </MDBox>
                </MDBox>
              </Grid>
            ))}

            {newFiles.map((file, index) => (
              <Grid item xs={12} sm={6} md={4} key={`new-${index}`}>
                <MDBox
                  sx={{
                    pt: 2,
                    pb: 2,
                    pl: 2,
                    pr: 1,
                    border: "1px solid #e0e0e0",
                    borderRadius: "4px",
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    width: "100%",
                    minHeight: "64px",
                  }}
                >
                  {previews[file.name] && (
                    <MDBox
                      component="img"
                      src={previews[file.name]}
                      alt={file.name}
                      width="40px"
                      height="40px"
                      borderRadius="4px"
                      sx={{ objectFit: "cover", flexShrink: 0 }}
                    />
                  )}
                  <MDBox flexGrow={1} sx={{ minWidth: 0 }}>
                    <MDTypography
                      variant="caption"
                      fontWeight="medium"
                      sx={{
                        lineHeight: 1.2,
                        wordBreak: "break-word",
                        display: "-webkit-box",
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: "vertical",
                        overflow: "hidden",
                      }}
                    >
                      {file.name}
                    </MDTypography>
                  </MDBox>
                  <MDBox display="flex" gap="4px">
                    {previews[file.name] && (
                      <MDButton
                        variant="text"
                        color="info"
                        size="small"
                        onClick={() => handleViewImage(previews[file.name], file.name)}
                        startIcon={<VisibilityIcon />}
                        sx={{ minWidth: "auto", p: 0.5 }}
                      ></MDButton>
                    )}
                    <MDButton
                      variant="text"
                      color="error"
                      size="small"
                      onClick={() => removeFile(index, false)}
                      startIcon={<DeleteIcon />}
                      sx={{ minWidth: "auto", p: 0.5 }}
                    ></MDButton>
                  </MDBox>
                </MDBox>
              </Grid>
            ))}
          </Grid>
        </MDBox>
      )}

      <Modal
        open={modalOpen}
        onClose={handleCloseModal}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <MDBox
          sx={{
            position: "relative",
            maxWidth: "90vw",
            maxHeight: "90vh",
            backgroundColor: "white",
            borderRadius: "8px",
            boxShadow: 24,
            overflow: "hidden",
          }}
        >
          <MDBox
            sx={{
              position: "absolute",
              top: 8,
              right: 8,
              zIndex: 1,
            }}
          >
            <IconButton
              onClick={handleCloseModal}
              sx={{
                backgroundColor: "rgba(0, 0, 0, 0.5)",
                color: "white",
                "&:hover": {
                  backgroundColor: "rgba(0, 0, 0, 0.7)",
                },
              }}
            >
              <CloseIcon />
            </IconButton>
          </MDBox>
          {selectedImage && (
            <>
              <MDBox p={2} borderBottom="1px solid #e0e0e0">
                <MDTypography variant="h6">{selectedImage.name}</MDTypography>
              </MDBox>
              <MDBox
                component="img"
                src={selectedImage.src}
                alt={selectedImage.name}
                sx={{
                  width: "100%",
                  height: "auto",
                  maxHeight: "calc(90vh - 80px)",
                  objectFit: "contain",
                }}
              />
            </>
          )}
        </MDBox>
      </Modal>
    </MDBox>
  );
}
